import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Plus, Trash2, MapPin, Calendar, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';

export default function BattleStylePage() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [poses, setPoses] = useState(user?.battle_poses || []);
  const [emotes, setEmotes] = useState(user?.battle_emotes || []);
  const [bio, setBio] = useState(user?.battle_bio || '');
  const [newPose, setNewPose] = useState('');
  const [newEmote, setNewEmote] = useState('');
  const [contests, setContests] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', event_datetime: '', venue_name: '',
    address: '', city: user?.city || '', state: user?.state || '', country: user?.country || 'MX',
    latitude: '', longitude: '', is_public: true,
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/profile/username/${user.username}/contests`);
        setContests(data.items || []);
      } catch {}
    })();
  }, [user.username]);

  const saveStyle = async () => {
    try {
      await api.patch('/profile/battle-style', { battle_bio: bio, poses, emotes });
      await refresh();
      toast.success('Estilo de batalla guardado');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  const addPose = () => { if (newPose.trim()) { setPoses((p) => [...p, newPose.trim()].slice(0, 30)); setNewPose(''); } };
  const addEmote = () => { if (newEmote.trim()) { setEmotes((p) => [...p, newEmote.trim()].slice(0, 40)); setNewEmote(''); } };

  const submitContest = async (e) => {
    e.preventDefault();
    try {
      const body = {
        ...form,
        latitude: form.latitude ? parseFloat(form.latitude) : null,
        longitude: form.longitude ? parseFloat(form.longitude) : null,
      };
      const { data } = await api.post('/contests', body);
      setContests((prev) => [...prev, data]);
      setShowForm(false);
      setForm({ ...form, title: '', description: '', event_datetime: '', venue_name: '', address: '', latitude: '', longitude: '' });
      toast.success('Contienda publicada');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  const del = async (id) => {
    if (!window.confirm('¿Eliminar contienda?')) return;
    try {
      await api.delete(`/contests/${id}`);
      setContests((prev) => prev.filter((c) => c.contest_id !== id));
    } catch { toast.error('Error'); }
  };

  return (
    <div className="min-h-screen bg-aura-bg pb-28 relative">
      <div className="absolute inset-0 bg-aura-radial pointer-events-none" />
      <header className="relative flex items-center justify-between px-5 pt-6">
        <button onClick={() => nav(-1)} className="text-aura-textDim flex items-center gap-1"><ChevronLeft size={20} /> Volver</button>
        <span className="font-display font-black tracking-[0.3em] aura-text-gradient text-sm">MI BATTLE STYLE</span>
        <span className="w-8" />
      </header>

      <main className="relative mx-auto max-w-md px-5 pt-4 space-y-5">
        {/* Battle Bio */}
        <section className="aura-card p-4">
          <div className="text-[11px] uppercase tracking-widest text-aura-textDim mb-2">Battle bio</div>
          <textarea value={bio} onChange={(e) => setBio(e.target.value)} maxLength={280} className="aura-input min-h-[80px] py-2" placeholder="¿Cómo peleas? ¿Cuál es tu vibra?" data-testid="battle-bio" />
        </section>

        {/* Poses */}
        <section className="aura-card p-4">
          <div className="text-[11px] uppercase tracking-widest text-aura-textDim mb-2">Poses ({poses.length}/30)</div>
          <div className="flex gap-2 flex-wrap mb-3">
            {poses.map((p, i) => (
              <span key={i} className="aura-chip !text-xs" data-testid={`pose-${i}`}>
                {p}
                <button onClick={() => setPoses(poses.filter((_, j) => j !== i))} className="ml-1 text-red-400/70"><X size={10} /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input value={newPose} onChange={(e) => setNewPose(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addPose())} maxLength={40} className="aura-input flex-1" placeholder="Ej: Aura Storm" data-testid="new-pose" />
            <button onClick={addPose} className="aura-btn-primary !py-2 !px-3"><Plus size={16} /></button>
          </div>
        </section>

        {/* Emotes */}
        <section className="aura-card p-4">
          <div className="text-[11px] uppercase tracking-widest text-aura-textDim mb-2">Emotes ({emotes.length}/40)</div>
          <div className="flex gap-2 flex-wrap mb-3">
            {emotes.map((e, i) => (
              <span key={i} className="aura-chip !text-lg" data-testid={`emote-${i}`}>
                {e}
                <button onClick={() => setEmotes(emotes.filter((_, j) => j !== i))} className="ml-1 text-red-400/70"><X size={10} /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input value={newEmote} onChange={(e) => setNewEmote(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addEmote())} maxLength={20} className="aura-input flex-1" placeholder="🔥 o :fire:" data-testid="new-emote" />
            <button onClick={addEmote} className="aura-btn-primary !py-2 !px-3"><Plus size={16} /></button>
          </div>
        </section>

        <button onClick={saveStyle} data-testid="save-style" className="aura-btn-primary w-full h-11">Guardar estilo</button>

        {/* Contests */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-display font-bold text-white text-sm tracking-widest">MIS CONTIENDAS</h3>
            <button onClick={() => setShowForm(true)} data-testid="new-contest" className="aura-btn-ghost !py-1.5 !px-3 !text-xs"><Plus size={12} /> Nueva</button>
          </div>
          {contests.length === 0 ? (
            <div className="aura-card p-6 text-center text-sm text-aura-textDim">Aún sin contiendas programadas.</div>
          ) : (
            <div className="space-y-2">
              {contests.map((c) => (
                <div key={c.contest_id} className="aura-card p-4" data-testid={`contest-${c.contest_id}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="font-display font-bold text-white flex-1 min-w-0 truncate">{c.title}</div>
                    <button onClick={() => del(c.contest_id)} className="text-red-400/70"><Trash2 size={14} /></button>
                  </div>
                  <div className="text-xs text-aura-textDim flex items-center gap-1 mb-1"><Calendar size={11} />{new Date(c.event_datetime).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })}</div>
                  <div className="text-xs text-aura-textDim flex items-center gap-1"><MapPin size={11} />{[c.venue_name, c.address, c.city, c.country].filter(Boolean).join(' · ')}</div>
                  {(c.latitude != null && c.longitude != null) && (
                    <iframe title="map" src={c.maps_embed} className="w-full h-40 rounded-lg mt-2 border border-white/10" loading="lazy" />
                  )}
                  <a href={c.maps_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-xs text-aura-cyan">Abrir en Google Maps →</a>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* New contest modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 backdrop-blur px-4">
          <form onSubmit={submitContest} className="w-full max-w-md aura-card p-5 space-y-3 overflow-y-auto max-h-[90vh]">
            <div className="flex items-center justify-between">
              <div className="font-display font-bold text-lg">Nueva contienda</div>
              <button type="button" onClick={() => setShowForm(false)}><X size={18} /></button>
            </div>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={80} placeholder="Título" className="aura-input" data-testid="c-title" />
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={500} placeholder="Descripción" className="aura-input min-h-[70px] py-2" />
            <input required type="datetime-local" value={form.event_datetime} onChange={(e) => setForm({ ...form, event_datetime: new Date(e.target.value).toISOString() })} className="aura-input [color-scheme:dark]" data-testid="c-datetime" />
            <input value={form.venue_name} onChange={(e) => setForm({ ...form, venue_name: e.target.value })} placeholder="Nombre del lugar (opcional)" className="aura-input" />
            <input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Dirección" className="aura-input" data-testid="c-address" />
            <div className="grid grid-cols-2 gap-2">
              <input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Ciudad" className="aura-input" data-testid="c-city" />
              <input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="Estado" className="aura-input" />
            </div>
            <input required value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })} maxLength={3} placeholder="País (MX)" className="aura-input" />
            <div className="grid grid-cols-2 gap-2">
              <input type="number" step="any" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} placeholder="Lat (opcional)" className="aura-input" />
              <input type="number" step="any" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} placeholder="Lng (opcional)" className="aura-input" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} className="h-4 w-4 accent-aura-cyan" />
              Publicar en el directorio
            </label>
            <button className="aura-btn-primary w-full" data-testid="c-publish">Publicar</button>
          </form>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
